import React, { useContext, useEffect, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { FiArrowRight, FiLogIn, FiUserPlus, FiHome, FiUser, FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { ShopContext } from '../../context/ShopContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';

const Register = () => {
  const { navigate, backendUrl, token, setToken } = useContext(ShopContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const googleAuth = JSON.parse(localStorage.getItem('googleAuth'));

    if (token || googleAuth) {
      setIsRedirecting(true);
      const timer = setTimeout(() => {
        navigate('/');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [token, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.password) {
      setModalMessage('All fields are required');
      setIsModalOpen(true);
      return false;
    }
    if (formData.password.length < 6) {
      setModalMessage('Password must be at least 6 characters long');
      setIsModalOpen(true);
      return false;
    }
    if (!agreeTerms) {
      setModalMessage('You must agree to the Terms and Conditions and Privacy Policy');
      setIsModalOpen(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setModalMessage('Authenticating...');
    setIsModalOpen(true);

    try {
      const response = await axios.post(
        `${backendUrl}/api/user/register`,
        formData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data?.success) {
        localStorage.setItem('token', response.data.token);
        setToken(response.data.token);
        localStorage.setItem('user', JSON.stringify({
          name: response.data.name || formData.name,
          email: response.data.email || formData.email
        }));
        setModalMessage('Registration successful!');
        setIsModalOpen(true);
        navigate('/');
      }
    } catch (error) {
      handleAuthError(error);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setModalMessage('Authenticating...');
    setIsModalOpen(true);

    try {
      const response = await axios.post(
        `${backendUrl}/api/user/auth/google`,
        { credential: credentialResponse.credential }
      );

      if (response.data?.success) {
        localStorage.setItem('token', response.data.token);
        setToken(response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('googleAuth', JSON.stringify(true));
        setModalMessage('Google login successful!');
        setIsModalOpen(true);
        navigate('/');
      }
    } catch (error) {
      handleAuthError(error);
    }
  };

  const handleAuthError = (error) => {
    console.error('Authentication error:', error);
    if (error.response) {
      setModalMessage(error.response.data?.message || 'Registration failed');
      setIsModalOpen(true);
    } else if (error.request) {
      setModalMessage('No response from server. Please try again.');
      setIsModalOpen(true);
    } else {
      setModalMessage('An unexpected error occurred');
      setIsModalOpen(true);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  if (isRedirecting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white">
        <div className="text-center p-8 max-w-md">
          <FiHome className="mx-auto text-5xl mb-4 text-gray-300" />
          <h2 className="text-2xl font-bold mb-2">Welcome Back!</h2>
          <p className="text-gray-300 mb-6">You're already logged in. Redirecting you to the homepage...</p>
          <div className="flex justify-center items-center text-gray-400">
            <FiArrowRight className="animate-pulse mr-2" />
            <span>Please wait</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Register | Sahla Curtain Center - Create Your Account</title>
        <meta name="description" content="Create your Sahla Curtain Center account for exclusive access to premium curtains, order tracking, and personalized offers. Join our community in Kerala today!" />
        <meta name="keywords" content="register, sign up, create account, Sahla Curtain Center, curtains, Kerala, customer registration, join, new user" />
        <link rel="canonical" href="https://sccstore.com/register" />
        {/* Open Graph */}
        <meta property="og:title" content="Register | Sahla Curtain Center - Create Your Account" />
        <meta property="og:description" content="Create your Sahla Curtain Center account for exclusive access to premium curtains, order tracking, and personalized offers. Join our community in Kerala today!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sccstore.com/register" />
        <meta property="og:image" content="https://sccstore.com/preview.jpg" />
        <meta property="og:site_name" content="Sahla Curtain Center" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Register | Sahla Curtain Center - Create Your Account" />
        <meta name="twitter:description" content="Create your Sahla Curtain Center account for exclusive access to premium curtains, order tracking, and personalized offers. Join our community in Kerala today!" />
        <meta name="twitter:image" content="https://sccstore.com/preview.jpg" />
        <meta name="twitter:site" content="@sccstore" />
        {/* Social Profiles */}
        <meta property="og:see_also" content="https://www.instagram.com/sahlacurtaincenter/" />
        <meta property="og:see_also" content="https://www.facebook.com/people/Sahla-Curtain-Center/61565451845405/" />
        <meta property="og:see_also" content="https://www.youtube.com/@sahlacurtaincenter" />
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Register | Sahla Curtain Center",
            "description": "Create your Sahla Curtain Center account for exclusive access to premium curtains, order tracking, and personalized offers. Join our community in Kerala today!",
            "url": "https://sccstore.com/register",
            "image": "https://sccstore.com/preview.jpg",
            "publisher": {
              "@type": "Organization",
              "name": "Sahla Curtain Center",
              "logo": {
                "@type": "ImageObject",
                "url": "https://sccstore.com/favicon.png"
              },
              "sameAs": [
                "https://www.instagram.com/sahlacurtaincenter/",
                "https://www.facebook.com/people/Sahla-Curtain-Center/61565451845405/",
                "https://www.youtube.com/@sahlacurtaincenter"
              ]
            }
          }
        `}</script>
      </Helmet>

      <form onSubmit={handleSubmit} className="flex flex-col items-center w-[90%] sm:max-w-96 m-auto mt-14 gap-4 text-gray-800">
        <div className='inline-flex items-center gap-2 mb-2 mt-10'>
          <p className='prata-regular text-3xl'>Sign Up</p>
          <hr className='border-none h-[1.5px] w-8 bg-gray-800' />
        </div>

        {/* Mini paragraph */}
        <p className="text-gray-500 text-sm mb-4 text-center">
          Join Sahla Curtain Center today and experience a world of elegance tailored for you.
        </p>

        {/* Name input */}
        <div className="relative w-full">
          <FiUser className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
          <input
            onChange={handleChange}
            type="text"
            name="name"
            value={formData.name}
            className="w-full pl-10 pr-3 py-2 border border-gray-800 bg-black text-white placeholder-gray-500"
            placeholder="Name"
            required
          />
        </div>

        {/* Email input */}
        <div className="relative w-full">
          <FiMail className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
          <input
            onChange={handleChange}
            type="email"
            name="email"
            value={formData.email}
            className="w-full pl-10 pr-3 py-2 border border-gray-800 bg-black text-white placeholder-gray-500"
            placeholder="Email"
            required
          />
        </div>

        {/* Password input with toggle */}
        <div className="relative w-full">
          <FiLock className="absolute top-1/2 left-3 transform -translate-y-1/2 text-gray-400" />
          <input
            onChange={handleChange}
            type={showPassword ? "text" : "password"}
            name="password"
            value={formData.password}
            className="w-full pl-10 pr-10 py-2 border border-gray-800 bg-black text-white placeholder-gray-500"
            placeholder="Password"
            required
            minLength={6}
          />
          <div
            onClick={() => setShowPassword(!showPassword)}
            className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-400 cursor-pointer"
          >
            {showPassword ? <FiEyeOff /> : <FiEye />}
          </div>
        </div>

        {/* Terms and Conditions Checkbox */}
        <div className="flex items-center w-full text-gray-500 text-xs mt-2">
          <input
            type="checkbox"
            id="terms"
            className="mr-2 accent-gray-800"
            checked={agreeTerms}
            onChange={() => setAgreeTerms(!agreeTerms)}
          />
          <label htmlFor="terms" className="leading-tight">
            I agree to the&nbsp;
            <Link to="/terms-and-conditions" className="text-gray-300 underline hover:text-gray-500">Terms and Conditions</Link>
            &nbsp;and&nbsp;
            <Link to="/privacy-policies" className="text-gray-300 underline hover:text-gray-500">Privacy Policy</Link>..
          </label>
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={!agreeTerms}
          className={`w-full font-light px-8 py-2 mt-4 border transition flex items-center justify-center gap-2 ${
            agreeTerms ? 'bg-black text-white border-gray-800 hover:bg-gray-900' : 'bg-gray-800 text-gray-400 cursor-not-allowed'
          }`}
        >
          <FiUserPlus /> Sign Up
        </button>

        {/* Google login */}
        <div className="w-full">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setModalMessage('Google login failed')}
            useOneTap
            theme="filled_black"
            size="large"
            text="continue_with"
            shape="rectangular"
            width="100%"
          />
        </div>

        {/* Link to login page */}
        <div className="text-center mt-4 text-gray-500">
          Already have an account?&nbsp;
          <Link to="/login" className="text-gray-300 underline hover:text-gary-500">
            Login
          </Link>
        </div>
      </form>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-lg max-w-sm w-full">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">{modalMessage}</h2>
            <div className="flex justify-center">
              <button
                onClick={closeModal}
                className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-900"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Register;
